import React, { useState } from 'react';
import { useAppStore } from "@/hooks/useAppStore";
import { copyToClipboard, generateTextTranscript, toggleFullscreen } from "@/lib/utils";
import { DisplayMessage, SystemMessageData } from "@/types";
import { useTranslation } from "@/lib/translate";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LogIn,
  Settings,
  Maximize,
  LogOut,
  Sun,
  Moon,
  SunMoon,
  Mic,
  LanguagesIcon,
  Drama,
  GraduationCap,
  Info
} from "lucide-react";

import PresentationIcon from "../icons/Presentation";
import { useTheme } from "../themes/ThemeProvider";
import { JoinRoomDialog } from "../dialogs/JoinRoomDialog";
import { SettingsDialog } from "../dialogs/SettingsDialog";
import { MicrophoneDialog } from "../dialogs/MicrophoneDialog";
import { ShareRoomDialog } from "../dialogs/ShareRoomDialog";
import { RoomHistoryDialog } from "../dialogs/RoomHistoryDialog";
import { TokenBalanceDialog } from "../dialogs/TokenBalanceDialog";
import { LanguageSettingsDialog } from "../dialogs/LanguageSettingsDialog";
import { ConversationContextDialog } from "../dialogs/ConversationContextDialog";
import './FloatingMenu.css';

interface FloatingMenuProps {
  onSettingsClick: () => void;
  onAccountClick: () => void;
  onPresentationClick: () => void;
  onFullscreenClick: () => void;
  onFontSizeClick: () => void;
}

export const FloatingMenu: React.FC<FloatingMenuProps> = ({
  onAccountClick,
  onFontSizeClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSizeOpen, setFontSizeOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const navigate = useNavigate();
  const { setTheme } = useTheme();

  // Dialog States
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [microphoneDialogOpen, setMicrophoneDialogOpen] = useState(false);
  const [languageSettingsDialogOpen, setLanguageSettingsDialogOpen] = useState(false);
  const [roomsHistoryDialogOpen, setRoomsHistoryDialogOpen] = useState(false);
  const [tokenbalanceDialogOpen, setTokenBalanceDialogOpen] = useState(false);
  const [conversationContextOpen, setConversationContextOpen] = useState(false);

  const clientConfig = useAppStore((state) => state.clientConfig);
  const setClientConfig = useAppStore((state) => state.setClientConfig);
  const currentFontSize = clientConfig?.fontSize || "normal";

  const roomId = useAppStore((state) => state.roomId);
  const createRoom = useAppStore((state) => state.createRoom);
  const messages = useAppStore((state) => state.messages);
  const targetLanguages = useAppStore((state) => state.targetLanguages);
  const showQosMessage = useAppStore((state) => state.showQosMessage);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const user = useAppStore((state) => state.user);
  const isPresentationMode = useAppStore((state) => state.isPresentationMode);
  const setPresentationMode = useAppStore((state) => state.setPresentationMode);
  
  const t = useTranslation(preferredLanguage);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
        setFontSizeOpen(false);
        setColorPickerOpen(false);
    }
  };

  /* Removed onFontSizeClick() to keep state local and fix popup behavior */
  const handleFontClick = () => {
    setFontSizeOpen(!fontSizeOpen);
    setColorPickerOpen(false);
  };

  const handleColorsClick = () => {
    setColorPickerOpen(!colorPickerOpen);
    setFontSizeOpen(false);
  };

  const handleFontSizeChange = (size: string) => {
      if (clientConfig) {
          setClientConfig({
              ...clientConfig,
              fontSize: size,
          });
      }
  };

  const handleDownloadClick = () => {
    const transcript = generateTextTranscript(roomId || "", messages as (DisplayMessage | SystemMessageData)[], targetLanguages[0] || "en");
    copyToClipboard(transcript);
    showQosMessage(t("Chat transcript has been copied to clipboard!"));
  };

  const handleAccountClickInternal = () => {
      if (user && user.provider === 'internal') {
          navigate(`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`);
      } else {
          // If logged in, maybe logout or go to account page? 
          // For now, let's assume logout as it matches the header's "other" auth action.
          navigate("/logout");
      }
      onAccountClick(); // Keep the original prop call just in case parent needs to know
  };

  const handleLogout = () => {
    navigate("/logout");
  };

  const handleLogin = () => {
    navigate(`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`);
  };

  const togglePresentationMode = () => {
    setPresentationMode(!isPresentationMode);
  }

  const handleVersionClick = () => {
    let version = import.meta.env.PACKAGE_VERSION;
    const protocol = window.location.protocol;
    if (protocol !== 'http:' && protocol !== 'https:') {
      version += 'n';
    }
    alert(`Version: ${version}`);
  };

  const handleResetTutorial = () => {
      if (window.confirm(t("Reset tutorial status and reload?"))) {
          localStorage.removeItem('chotto_tutorial_completed');
          localStorage.removeItem('chotto_mic_permission_granted');
          window.location.reload();
      }
  };

  const menuItems = [
    {
      id: 'context',
      label: 'conversation context',
      icon: <Drama className="w-6 h-6 text-white z-[1]" />,
      onClick: () => setConversationContextOpen(true),
    },
    {
      id: 'download',
      label: 'download',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="w-6 h-6 fill-white z-[1]">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
        </svg>
      ),
      onClick: handleDownloadClick,
    },
    {
      id: 'font',
      label: 'text size',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="w-6 h-6 fill-white z-[1]">
          <path d="M2.5 4v3h5v12h3V7h5V4h-13zm19 5h-9v3h3v7h3v-7h3V9z"/>
        </svg>
      ),
      onClick: handleFontClick,
    },
    // {
    //   id: 'colors',
    //   label: 'colors',
    //   icon: (
    //     <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="w-6 h-6 fill-white z-[1]">
    //       <path d="M12 3a9 9 0 0 0 0 18c.83 0 1.5-.67 1.5-1.5 0-.32-.13-.62-.34-.85-.27-.28-.44-.67-.44-1.1 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    //     </svg>
    //   ),
    //   onClick: handleColorsClick,
    // },
    {
      id: 'settings',
      label: 'settings',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="w-6 h-6 fill-white z-[1]">
          <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L3.16 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
        </svg>
      ),
      isDropdown: true,
    },
    // {
    //   id: 'account',
    //   label: 'my account',
    //   icon: (
    //     <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="w-6 h-6 fill-white z-[1]">
    //       <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    //     </svg>
    //   ),
    //   onClick: handleAccountClickInternal,
    // }
  ];

  const fontIndex = menuItems.findIndex(item => item.id === 'font');
  const fontMenuTop = fontIndex >= 0 ? 160 + (fontIndex * 80) : 240;


  return (
    <>
      <button 
        className={`settings-toggle-btn fixed left-5 z-[11] w-14 h-14 rounded-full text-white cursor-pointer flex items-center justify-center text-[22px] pointer-events-auto border-2 border-white/30 ${isOpen ? 'active' : ''}`} 
        style={{ top: 'calc(80px + env(safe-area-inset-top))' }}
        id="settingsToggleBtn" 
        onClick={toggleMenu}
        aria-label={isOpen ? "Close menu" : "Open menu"}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>

      {/* Menu buttons */}
      {menuItems.map((item, index) => {
        const top = 160 + (index * 80);
        const delay = index * 50;
        
        const button = (
          <button 
            key={item.id}
            className={`settings-menu-btn fixed left-5 z-10 w-[50px] h-[50px] rounded-full text-white cursor-pointer flex items-center justify-center text-[22px] pointer-events-auto bg-transparent border-none ${isOpen ? 'visible' : ''}`} 
            id={`${item.id}Btn`} 
            style={{ top: `calc(${top}px + env(safe-area-inset-top))`, transitionDelay: isOpen ? `${delay}ms` : '0ms' }} 
            onClick={item.onClick}
          >
              <div className="btn-visual absolute inset-0 rounded-full border-2 border-white/30 -z-10"></div>
              {item.icon}
              <span className="menu-tooltip absolute left-[70px] top-1/2 -translate-y-1/2 text-white px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap pointer-events-none z-[100] opacity-0">{item.label}</span>
          </button>
        );

        if (item.isDropdown) {
          return (
            <DropdownMenu key={item.id}>
              <DropdownMenuTrigger asChild>
                {button}
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                side="right" 
                sideOffset={10}
                className="w-56 glass-dropdown border-0"
                usePortal={false}
                alignOffset={-320}
              >
                  <DropdownMenuItem onSelect={toggleFullscreen} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <Maximize className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("Toggle Fullscreen")}</span>
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem onSelect={togglePresentationMode} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <PresentationIcon />
                    <span>{t("Enter Presentation Mode [F5]")}</span>
                  </DropdownMenuItem> */}
                  {/* <DropdownMenuSeparator className="bg-white/20" />
                  <DropdownMenuItem onSelect={() => setTheme("light")} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <Sun className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("Light Mode")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setTheme("dark")} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <Moon className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("Dark Mode")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setTheme("system")} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <SunMoon className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("System Mode")}</span>
                  </DropdownMenuItem> */}
                  <DropdownMenuSeparator className="bg-white/20" />
                  <DropdownMenuItem onSelect={() => setSettingsDialogOpen(true)} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <Settings className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("Settings")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setMicrophoneDialogOpen(true)} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <Mic className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("Microphone")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setLanguageSettingsDialogOpen(true)} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <LanguagesIcon className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("Set Interface Language")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={handleResetTutorial} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <GraduationCap className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("Tutorial")}</span>
                  </DropdownMenuItem>
                  {/* {user && user.provider !== "internal" ? (
                    <DropdownMenuItem onSelect={() => handleLogout()} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                      <LogOut className="mr-2 h-4 w-4 opacity-100" />
                      <span>{t("Logout")}</span>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={handleLogin} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                      <LogIn className="mr-2 h-4 w-4 opacity-100" />
                      <span>{t("Login")}</span>
                    </DropdownMenuItem>
                  )} */}
                  <DropdownMenuItem onSelect={handleVersionClick} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    <Info className="mr-2 h-4 w-4 opacity-100" />
                    <span>{t("Version")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/20" />
                  <DropdownMenuItem onSelect={() => window.open("https://akavirtual.com/", "_blank")} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    {t("Created by AKA Virtual")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => window.open("https://shisa.ai/", "_blank")} className="cursor-pointer glass-dropdown-item focus:bg-white/10 focus:text-white">
                    {t("Powered by Shisa.ai")}
                  </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }

        return button;
      })}

      {/* Font size selector */}
      <div 
        className={`fixed left-[80px] z-10 flex flex-row gap-2 p-1.5 rounded-[25px] border-2 border-white/30 bg-white/15 backdrop-blur-[10px] shadow-lg transition-all duration-300 ease-out`} 
        id="fontSizeSelector"
        style={{ 
          top: `calc(${fontMenuTop + 25}px + env(safe-area-inset-top))`,
          transform: fontSizeOpen ? 'translate(0, -50%)' : 'translate(-20px, -50%)',
          opacity: fontSizeOpen ? 1 : 0,
          pointerEvents: fontSizeOpen ? 'auto' : 'none',
        }}
      >
          <button 
            className={`w-10 h-10 rounded-full text-white cursor-pointer flex items-center justify-center font-semibold border-2 border-white/30 transition-all duration-200 hover:bg-white/30 hover:scale-110 ${currentFontSize === 'normal' ? 'bg-[#4dd0e1]/50 border-[#4dd0e1]/80' : 'bg-white/20'}`} 
            onClick={() => handleFontSizeChange('normal')}
          >M</button>
          <button 
            className={`w-10 h-10 rounded-full text-white cursor-pointer flex items-center justify-center font-semibold border-2 border-white/30 transition-all duration-200 hover:bg-white/30 hover:scale-110 ${currentFontSize === 'large' ? 'bg-[#4dd0e1]/50 border-[#4dd0e1]/80' : 'bg-white/20'}`} 
            onClick={() => handleFontSizeChange('large')}
          >L</button>
          <button 
            className={`w-10 h-10 rounded-full text-white cursor-pointer flex items-center justify-center font-semibold border-2 border-white/30 transition-all duration-200 hover:bg-white/30 hover:scale-110 ${currentFontSize === 'xlarge' ? 'bg-[#4dd0e1]/50 border-[#4dd0e1]/80' : 'bg-white/20'}`} 
            onClick={() => handleFontSizeChange('xlarge')}
          >XL</button>
      </div>

      {/* Color picker container */}
      <div className={`color-picker-container fixed left-[90px] right-auto z-10 hidden flex-col gap-2 pointer-events-auto ${colorPickerOpen ? 'visible' : ''}`} id="colorPickerContainer" style={{ top: 'calc(150px + env(safe-area-inset-top))' }}>
          <div className="color-picker-label text-white text-xs font-semibold tracking-[0.05em] uppercase px-3 py-2 rounded-[5px] border border-white/30">Background Color</div>
          <div className="color-picker-wrapper flex items-center gap-2.5 px-3 py-2 rounded-[5px] border border-white/30">
              <input type="color" id="bgColorPicker" className="color-picker-input w-[60px] h-10 border-2 border-white/50 rounded-[5px] cursor-pointer bg-transparent" defaultValue="#59d9d7" onChange={(e) => {
                  const event = new CustomEvent('backgroundColorChange', { detail: { color: e.target.value } });
                  window.dispatchEvent(event);
                  const valEl = document.getElementById('colorValue');
                  if (valEl) valEl.textContent = e.target.value;
              }} />
              <span className="color-value text-white text-[11px] font-mono tracking-[0.05em]" id="colorValue">#59d9d7</span>
          </div>
          <div className="color-picker-label text-white text-xs font-semibold tracking-[0.05em] uppercase px-3 py-2 rounded-[5px] border border-white/30 mt-3">Jia's Bubble Color</div>
          <div className="color-picker-wrapper flex items-center gap-2.5 px-3 py-2 rounded-[5px] border border-white/30">
              <input type="color" id="jiaColorPicker" className="color-picker-input w-[60px] h-10 border-2 border-white/50 rounded-[5px] cursor-pointer bg-transparent" defaultValue="#ebf8ff" onChange={(e) => {
                  document.documentElement.style.setProperty('--jia-color', e.target.value);
                  const valEl = document.getElementById('jiaColorValue');
                  if (valEl) valEl.textContent = e.target.value;
              }} />
              <span className="color-value text-white text-[11px] font-mono tracking-[0.05em]" id="jiaColorValue">#ebf8ff</span>
          </div>
          <div className="color-picker-label text-white text-xs font-semibold tracking-[0.05em] uppercase px-3 py-2 rounded-[5px] border border-white/30 mt-3">Ryo's Bubble Color</div>
          <div className="color-picker-wrapper flex items-center gap-2.5 px-3 py-2 rounded-[5px] border border-white/30">
              <input type="color" id="ryoColorPicker" className="color-picker-input w-[60px] h-10 border-2 border-white/50 rounded-[5px] cursor-pointer bg-transparent" defaultValue="#fff5eb" onChange={(e) => {
                  document.documentElement.style.setProperty('--ryo-color', e.target.value);
                  const valEl = document.getElementById('ryoColorValue');
                  if (valEl) valEl.textContent = e.target.value;
              }} />
              <span className="color-value text-white text-[11px] font-mono tracking-[0.05em]" id="ryoColorValue">#fff5eb</span>
          </div>
      </div>

      {/* Render Dialogs */}
      <JoinRoomDialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen} />
      <ShareRoomDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
      />
      <SettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen} />
      <MicrophoneDialog
        open={microphoneDialogOpen}
        onOpenChange={setMicrophoneDialogOpen}
      />
      <LanguageSettingsDialog
        open={languageSettingsDialogOpen}
        onOpenChange={setLanguageSettingsDialogOpen}
      />
      <RoomHistoryDialog
        open={roomsHistoryDialogOpen}
        onOpenChange={setRoomsHistoryDialogOpen}
      />
      <TokenBalanceDialog
        open={tokenbalanceDialogOpen}
        onOpenChange={setTokenBalanceDialogOpen}
      />
      <ConversationContextDialog
        open={conversationContextOpen}
        onOpenChange={setConversationContextOpen}
      />
    </>
  );
};
